import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const TIME_FILTERS = ['1W', '1M', '3M', '1Y'];

const generateData = (period) => {
  const points = { '1W': 7, '1M': 30, '3M': 90, '1Y': 52 };
  const labels = {
    '1W': ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    '1M': Array.from({ length: 30 }, (_, i) => `Feb ${i + 1}`),
    '3M': Array.from({ length: 12 }, (_, i) => ['Dec', 'Jan', 'Feb', 'Mar']?.[Math.floor(i / 3)] + ` W${(i % 3) + 1}`),
    '1Y': ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb']?.map((m, i) => `${m} '${i < 9 ? '25' : '26'}`),
  };
  const n = points?.[period];
  let val = 42000;
  return Array.from({ length: n }, (_, i) => {
    val += (Math.random() - 0.42) * 800;
    return { date: labels?.[period]?.[i] || `P${i + 1}`, value: Math.max(35000, val) };
  });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload?.length) {
    return (
      <div className="rounded-lg shadow-lg px-3 py-2 border" style={{ background: 'var(--color-primary)', borderColor: 'rgba(133,113,77,0.3)' }}>
        <p className="font-caption text-white/70 text-xs">{label}</p>
        <p className="font-data font-bold text-sm" style={{ color: 'var(--color-accent)' }}>
          ${payload?.[0]?.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      </div>
    );
  }
  return null;
};

export default function PerformanceChart() {
  const [activeFilter, setActiveFilter] = useState('1M');
  const data = generateData(activeFilter);
  const startVal = data?.[0]?.value || 0;
  const endVal = data?.[data?.length - 1]?.value || 0;
  const isPositive = endVal >= startVal;

  return (
    <div className="rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading font-semibold text-lg md:text-xl" style={{ color: 'var(--color-foreground)' }}>Performance</h2>
          <p className={`font-caption text-sm mt-0.5 ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
            {isPositive ? '▲' : '▼'} ${Math.abs(endVal - startVal)?.toLocaleString('en-US', { minimumFractionDigits: 2 })} ({isPositive ? '+' : ''}{(((endVal - startVal) / startVal) * 100)?.toFixed(2)}%) this period
          </p>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: 'var(--color-muted)' }}>
          {TIME_FILTERS?.map(f => (
            <button key={f} onClick={() => setActiveFilter(f)}
              className="px-3 py-1.5 rounded-md font-caption text-xs font-semibold transition-all duration-200"
              style={{
                background: activeFilter === f ? 'var(--color-background)' : 'transparent',
                color: activeFilter === f ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                boxShadow: activeFilter === f ? 'var(--shadow-sm)' : 'none',
              }}>
              {f}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-48 md:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,48,87,0.08)" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: 'var(--font-caption)', fill: 'var(--color-muted-foreground)' }}
              tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fontFamily: 'var(--font-data)', fill: 'var(--color-muted-foreground)' }}
              tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000)?.toFixed(0)}k`} width={45} />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={startVal} stroke="rgba(133,113,77,0.3)" strokeDasharray="4 4" />
            <Line type="monotone" dataKey="value" stroke="var(--color-primary)" strokeWidth={2.5}
              dot={(props) => {
                const { cx, cy, index } = props;
                if (index === data?.length - 1) return <circle key={`dot-${index}`} cx={cx} cy={cy} r={5} fill="var(--color-accent)" stroke="white" strokeWidth={2} />;
                return <circle key={`dot-${index}`} cx={cx} cy={cy} r={0} />;
              }}
              activeDot={{ r: 6, fill: 'var(--color-accent)', stroke: 'white', strokeWidth: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}